import React, { useEffect, useMemo, useRef } from 'react';
import { useMap } from 'react-leaflet';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import RefreshIcon from '@mui/icons-material/Refresh';
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Tooltip as LeafletTooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { isValidGpsCoord, findAtPlaybackTime } from './visualizeData.constants';

/** Accessor used with findClosestByTime for gpsFrames arrays. */
const getGpsTs = (frame) => frame.time;

// Fix default Leaflet marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom car icon — created once at module level so Leaflet doesn't
// re-create the DOM element on every render during playback.
const CAR_ICON = L.divIcon({
  className: 'car-marker-icon',
  html: `<div style="
    width: 20px; height: 20px;
    background: #1976d2;
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(25,118,210,0.5);
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

/** FitBounds — helper component to fit the map to given bounds */
const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 19 });
    }
  }, [map, bounds]);
  return null;
};

/** PanToPosition — smoothly pans to the current vehicle position */
const PanToPosition = ({ position, follow }) => {
  const map = useMap();
  useEffect(() => {
    if (follow && position) {
      map.panTo(position, { animate: true, duration: 0.3 });
    }
  }, [map, position, follow]);
  return null;
};

/**
 * TrailPolyline — renders the driven trail with zoom-aware decimation.
 *
 * At low zoom levels (zoomed out) we skip every Nth point so Leaflet
 * isn't pushing thousands of vertices to the SVG renderer each frame.
 * At high zoom (zoomed in) we render every point for full fidelity.
 *
 * Zoom → step:  ≤12 → 16,  ≤14 → 8,  ≤16 → 4,  >16 → 1 (every point)
 */
const TrailPolyline = ({ trail }) => {
  const map = useMap();
  const zoom = map.getZoom();

  const decimated = useMemo(() => {
    if (trail.length < 2) return trail;
    const step = zoom <= 12 ? 16 : zoom <= 14 ? 8 : zoom <= 16 ? 4 : 1;
    if (step === 1) return trail;
    // Always keep the last point so the trail ends exactly at the car
    const result = trail.filter((_, i) => i % step === 0);
    if (result[result.length - 1] !== trail[trail.length - 1]) {
      result.push(trail[trail.length - 1]);
    }
    return result;
  }, [trail, zoom]);

  if (decimated.length < 2) return null;
  return <Polyline positions={decimated} color="#1976d2" weight={4} opacity={0.8} />;
};

/**
 * MapPanel — renders the vehicle route and current position on a Leaflet map.
 *
 * Props:
 *   routePositions  — [[lat,lng], …]  full route polyline
 *   gpsFrames       — array of frame objects with { time, lat, lng }
 *   currentTimeSec  — elapsed playback time in seconds (from usePlayback)
 *   bounds          — [[minLat,minLng],[maxLat,maxLng]]
 *   isLoading       — whether data is still loading
 *   isError         — whether an error occurred
 *   error           — the error object
 *   onRefetch       — callback to retry fetching
 *   globalStartTs   — authoritative file start timestamp (ns) from backend
 */
const MapPanel = ({
  routePositions = [],
  gpsFrames = [],
  currentTimeSec = 0,
  bounds = null,
  isLoading = false,
  isError = false,
  error = null,
  onRefetch,
  globalStartTs,
}) => {
  const mapRef = useRef(null);
  const [followCar, setFollowCar] = React.useState(true);

  // Find the GPS frame nearest to currentTimeSec
  const currentFrame = useMemo(
    () => findAtPlaybackTime(gpsFrames, currentTimeSec, getGpsTs, globalStartTs),
    [gpsFrames, currentTimeSec, globalStartTs],
  );

  // Determine the current vehicle position from the nearest GPS frame
  const vehiclePosition = useMemo(() => {
    if (!currentFrame) return null;
    if (isValidGpsCoord(currentFrame.lat, currentFrame.lng)) {
      return [currentFrame.lat, currentFrame.lng];
    }
    return null;
  }, [currentFrame]);

  // Draw current driven "trail" — driven route up to the current frame
  const trail = useMemo(() => {
    if (!currentFrame) return [];
    const idx = gpsFrames.indexOf(currentFrame);
    return gpsFrames.slice(0, idx + 1).map((f) => [f.lat, f.lng]);
  }, [gpsFrames, currentFrame]);

  // Start / end markers
  const startPos = routePositions.length > 0 ? routePositions[0] : null;
  const endPos = routePositions.length > 1 ? routePositions[routePositions.length - 1] : null;

  const handleRecenter = () => {
    if (mapRef.current && bounds) {
      mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 19 });
    }
    setFollowCar(true);
  };

  // ---- Loading state ----
  if (isLoading) {
    return (
      <Box className="map-panel-container map-panel-loading">
        <CircularProgress size={36} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Loading GPS data…
        </Typography>
      </Box>
    );
  }

  // ---- Error state ----
  if (isError) {
    return (
      <Box className="map-panel-container map-panel-error">
        <Alert
          severity="error"
          action={
            onRefetch && (
              <IconButton size="small" onClick={onRefetch}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            )
          }
        >
          {error?.message || 'Failed to load GPS data'}
        </Alert>
      </Box>
    );
  }

  // ---- No GPS data ----
  if (routePositions.length === 0) {
    return (
      <Box className="map-panel-container map-panel-empty">
        <MyLocationIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No GPS data available in this file.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          GPS signals (RPPSDBG3P_LatAuth, GPSC_PPSLat, etc.) were not found.
        </Typography>
      </Box>
    );
  }

  // Default center if no bounds
  const defaultCenter = routePositions[Math.floor(routePositions.length / 2)] || [42.3314, -83.0458]; // Detroit

  return (
    <Box className="map-panel-container">
      {/* Header */}
      <Box className="map-panel-header">
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
          Vehicle Map
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip
            label={`${gpsFrames.length} GPS pts`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
          <Tooltip title={followCar ? 'Following vehicle' : 'Click to follow vehicle'}>
            <IconButton
              size="small"
              onClick={() => setFollowCar((prev) => !prev)}
              color={followCar ? 'primary' : 'default'}
            >
              <MyLocationIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Recenter map">
            <IconButton size="small" onClick={handleRecenter}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Map */}
      <Box className="map-panel-map-wrapper">
        <MapContainer
          center={defaultCenter}
          zoom={14}
          className="map-panel-leaflet"
          ref={mapRef}
          whenReady={(mapInstance) => {
            mapRef.current = mapInstance.target;
          }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Fit bounds on first load */}
          {bounds && <FitBounds bounds={bounds} />}

          {/* Follow car when playing */}
          <PanToPosition position={vehiclePosition} follow={followCar} />

          {/* Full route (light grey) */}
          <Polyline positions={routePositions} color="#b0bec5" weight={3} opacity={0.5} />

          {/* Driven trail (blue) — zoom-decimated for performance */}
          <TrailPolyline trail={trail} />

          {/* Start marker */}
          {startPos && (
            <CircleMarker center={startPos} radius={6} pathOptions={{ color: '#2e7d32', fillColor: '#4caf50', fillOpacity: 1, weight: 2 }}>
              <LeafletTooltip direction="top" offset={[0, -8]} permanent={false}>
                Start
              </LeafletTooltip>
            </CircleMarker>
          )}

          {/* End marker */}
          {endPos && (
            <CircleMarker center={endPos} radius={6} pathOptions={{ color: '#c62828', fillColor: '#ef5350', fillOpacity: 1, weight: 2 }}>
              <LeafletTooltip direction="top" offset={[0, -8]} permanent={false}>
                End
              </LeafletTooltip>
            </CircleMarker>
          )}

          {/* Current vehicle position */}
          {vehiclePosition && (
            <Marker position={vehiclePosition} icon={CAR_ICON} />
          )}
        </MapContainer>
      </Box>

      {/* Current coords */}
      {vehiclePosition && (
        <Box className="map-panel-coords">
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>
            {vehiclePosition[0].toFixed(6)}, {vehiclePosition[1].toFixed(6)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MapPanel;
